import { useMemo } from 'react'
import useStore from '@/stores/useStore'
import { cn, formatCurrency } from '@/lib/utils'

// Pro-rata-vs-design comparison (plan Part D): the TPA-standard "who does this
// design favor" view.
//
// One question, asked in dollars: if the design's own profit-sharing total were
// handed out as a flat percent of pay instead, who would get more and who would
// get less? Both columns total the same money, so this is only ever about WHO.
// It is how a new-comparability or integrated design's tilt toward owners
// becomes arguable without arguing about formulas.
//
// Vocabulary discipline: the baseline is called "an even split of pay"
// everywhere, in the heading, the prose, and the column. Earlier copy called it
// three different things in four sentences and read as three separate ideas.

const fmtPct = (value) => `${(Number(value) || 0).toFixed(2)}%`

// Below this the design is an even split in all but rounding, and the table
// would be a column of zeros dressed up as an insight. A plain pro-rata design
// lands at exactly 0 and never renders.
const MIN_TILT_SHARE = 0.005

export default function AllocationComparisonTable({ planKey = 'planA' }) {
  const calculatedEmployees = useStore((state) => state.analysis[planKey]?.calculatedEmployees || state.analysis.planA?.calculatedEmployees)

  const model = useMemo(() => {
    const employees = calculatedEmployees || []
    const totalDesign = employees.reduce((sum, row) => sum + (Number(row.profitSharingAmount) || 0), 0)
    // The pool a comp-to-comp design would spread: everyone eligible for
    // employer money with positive capped compensation.
    const participants = employees.filter((row) => (
      row.eligibility?.allowsEmployerContributions === true
      && (Number(row.cappedCompensation) || 0) > 0
      && row.status !== 'Terminated'
    ))
    const totalComp = participants.reduce((sum, row) => sum + (Number(row.cappedCompensation) || 0), 0)
    const rows = participants
      .map((row) => {
        const comp = Number(row.cappedCompensation) || 0
        const design = Number(row.profitSharingAmount) || 0
        const proRata = totalComp > 0 ? (totalDesign * comp) / totalComp : 0
        return {
          id: row.id,
          name: row.name || row.id,
          isOwner: Boolean(row.isFivePercentOwner),
          comp,
          design,
          proRata,
          diff: design - proRata,
        }
      })
      .sort((a, b) => b.diff - a.diff || b.design - a.design)

    // Dollars the design moves. Gains and losses are mirror images (both
    // columns total the same pool), so one number describes the whole tilt.
    const gainers = rows.filter((row) => row.diff > 0.5)
    const tilt = gainers.reduce((sum, row) => sum + row.diff, 0)
    const ownerTilt = gainers.filter((row) => row.isOwner).reduce((sum, row) => sum + row.diff, 0)

    return { totalDesign, totalComp, rows, tilt, ownerTilt, gainers, losers: rows.filter((row) => row.diff < -0.5) }
  }, [calculatedEmployees])

  if (model.totalDesign <= 0 || model.rows.length < 2) return null
  // An even-split design has nothing to compare against an even split.
  if (model.tilt < model.totalDesign * MIN_TILT_SHARE) return null

  // The headline is the finding, not the method. Owners are the question this
  // table exists to answer, so they lead whenever the tilt actually favors them.
  const ownerCount = model.gainers.filter((row) => row.isOwner).length
  const headline = ownerCount > 0 && model.ownerTilt > 0
    ? `${formatCurrency(model.ownerTilt)} steered toward ${ownerCount} owner${ownerCount === 1 ? '' : 's'}`
    : `${formatCurrency(model.tilt)} steered toward ${model.gainers.length} of ${model.rows.length} people`

  return (
    <details className="rounded-md border border-border" data-testid="allocation-comparison-table">
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
        Who this design favors
        <span className="ml-2 font-normal text-muted-foreground">{headline}</span>
      </summary>
      <div className="border-t border-border px-4 py-3">
        <p className="mb-2 max-w-3xl text-xs leading-relaxed text-muted-foreground">
          Your design hands out {formatCurrency(model.totalDesign)}. This shows what each person would get if those
          same dollars were split as a flat percent of pay instead, and how far the design moves them from that
          baseline. Both columns total the same money, so a positive difference for one person is always someone
          else&rsquo;s negative. The even-split column does not reapply hours or last-day conditions, so read it as the
          shape of the design, not a quote.
        </p>
        <div className="max-h-96 overflow-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card text-muted-foreground">
              <tr className="border-b">
                <th className="p-2 text-left font-medium">Employee</th>
                <th className="p-2 text-right font-medium">Eligible comp</th>
                <th className="p-2 text-right font-medium">This design</th>
                <th className="p-2 text-right font-medium">Design %</th>
                <th className="p-2 text-right font-medium">Even split of pay</th>
                <th className="p-2 text-right font-medium">Difference</th>
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="p-2 text-foreground">
                    {row.name}
                    {row.isOwner && <span className="ml-1.5 rounded bg-muted px-1 text-[10px] font-semibold text-muted-foreground">owner</span>}
                  </td>
                  <td className="p-2 text-right tabular-nums">{formatCurrency(row.comp)}</td>
                  <td className="p-2 text-right tabular-nums">{formatCurrency(row.design)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtPct(row.comp > 0 ? (row.design / row.comp) * 100 : 0)}</td>
                  <td className="p-2 text-right tabular-nums text-muted-foreground">{formatCurrency(row.proRata)}</td>
                  {/* Both directions are colored: the give and the take are the
                      same fact, so greying one side hid half the story. */}
                  <td className={cn(
                    'p-2 text-right tabular-nums',
                    row.diff > 0.5 ? 'text-emerald-700 dark:text-emerald-300'
                      : row.diff < -0.5 ? 'text-amber-700 dark:text-amber-300'
                        : 'text-muted-foreground',
                  )}>
                    {row.diff > 0.5 ? '+' : ''}{formatCurrency(row.diff)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t bg-muted/30 font-medium">
              <tr>
                <td className="p-2">Total</td>
                <td className="p-2 text-right tabular-nums">{formatCurrency(model.totalComp)}</td>
                <td className="p-2 text-right tabular-nums">{formatCurrency(model.totalDesign)}</td>
                <td className="p-2" />
                <td className="p-2 text-right tabular-nums">{formatCurrency(model.totalDesign)}</td>
                <td className="p-2 text-right tabular-nums text-muted-foreground">$0</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </details>
  )
}
